import { useState } from "react";
import {
  CheckCircle2,
  Clock,
  FileSearch,
  Loader2,
  Lock,
  MapPin,
  Play,
  RefreshCcw,
  Sparkles,
} from "lucide-react";
import type { CfoQueueCounts } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PRESETS = [5, 10, 20, 50] as const;
const DEFAULT_SIZE = 10;

function Tile({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof Lock;
  label: string;
  value: number | null;
  hint: string;
  tone: "sky" | "amber" | "slate";
}) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-500",
  } as const;
  return (
    <div className="rounded-xl border border-slate-900/[0.06] bg-white p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        <span className={cn("flex h-5 w-5 items-center justify-center rounded-md", tones[tone])}>
          <Icon className="h-3 w-3" />
        </span>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
        {value === null ? "…" : value}
      </p>
      <p className="text-[11px] text-slate-500">{hint}</p>
    </div>
  );
}

/** Before a session: what the CFO research queue holds, and a lot to reserve.
 *  The lot's ceiling is the project's; an over-sized request comes back
 *  refused with that ceiling named, shown as it is. */
export function CfoWorkSetup({
  counts,
  isOpening,
  error,
  onStart,
  onRefresh,
}: {
  counts: CfoQueueCounts | null;
  isOpening: boolean;
  error: string | null;
  onStart: (count: number) => void;
  onRefresh: () => void;
}) {
  const [raw, setRaw] = useState(String(DEFAULT_SIZE));
  const parsed = Number.parseInt(raw.trim(), 10);
  const size = Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
  const available = counts?.available ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section className="space-y-5 rounded-plane bg-white p-6 shadow-plane">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <FileSearch className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">CFO research session</h2>
              <p className="text-sm text-slate-500">
                Companies whose CFO decision waits for a person to review: a founder place or a
                school only a person can place. You get everything the sources already say, and
                where to look for the rest.
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">How many companies?</p>
            <div className="flex flex-wrap items-center gap-2">
              {PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRaw(String(n))}
                  className={cn(
                    "rounded-lg border px-3.5 py-2 text-sm font-medium tabular-nums transition-all",
                    size === n
                      ? "border-indigo-500 bg-indigo-500 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {n}
                </button>
              ))}
              <Input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                aria-label="Batch size"
                className="h-[38px] w-24 tabular-nums"
              />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              The companies are held for you while you work: nobody else is handed them. What
              you close is released at once; the rest when you end the session.
            </p>
          </div>

          {error && (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {available === null ? (
                "Counting the queue…"
              ) : available === 0 ? (
                "Nothing to research right now."
              ) : (
                <>
                  <span className="font-semibold tabular-nums text-indigo-600">{available}</span>{" "}
                  compan{available === 1 ? "y" : "ies"} to research
                </>
              )}
            </p>
            <Button
              size="lg"
              variant="primary"
              disabled={isOpening || size === null || available === 0}
              onClick={() => size !== null && onStart(size)}
            >
              {isOpening ? <Loader2 className="animate-spin" /> : <Play />}
              {size === null ? "Start session" : `Start session (${size})`}
            </Button>
          </div>
        </section>

        <aside className="space-y-3 rounded-plane bg-slate-50/80 p-5 ring-1 ring-inset ring-slate-900/[0.04]">
          <p className="text-sm font-semibold text-slate-900">How it works</p>
          <ol className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
              <span>
                Each company shows what its decision waits for, and what the sources already
                found: founders and their places, team, job postings, funding, investors.
              </span>
            </li>
            <li className="flex gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
              <span>
                Search with the ready-made links, then answer in place, with where you read it.
                The company is re-scored with your answer.
              </span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
              <span>
                <strong>Done</strong> once answered, <strong>Can&rsquo;t find</strong> with a
                note, or <strong>Skip</strong> to leave it to someone else.
              </span>
            </li>
          </ol>
        </aside>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700">The queue now</p>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600"
          >
            <RefreshCcw className="h-3 w-3" />
            Refresh
          </button>
        </div>
        {/* Missing data is researched only by a project that asks for it (owner, 2026-09-28). */}
        <div
          className={cn(
            "grid grid-cols-2 gap-3",
            counts?.data ? "md:grid-cols-5" : "md:grid-cols-4",
          )}
        >
          <Tile
            icon={MapPin}
            label="To review"
            value={counts?.review ?? null}
            hint="A founder place decides"
            tone="sky"
          />
          {!!counts?.data && (
            <Tile
              icon={FileSearch}
              label="Missing data"
              value={counts.data}
              hint="A scoring input is missing"
              tone="amber"
            />
          )}
          <Tile
            icon={Lock}
            label="Held"
            value={counts?.held ?? null}
            hint="In a session right now"
            tone="slate"
          />
          <Tile
            icon={Clock}
            label="Can't find"
            value={counts?.parked ?? null}
            hint="Back with new data"
            tone="slate"
          />
          <Tile
            icon={RefreshCcw}
            label="Re-scoring"
            value={counts?.reevaluating ?? null}
            hint="Answered, being re-scored"
            tone="slate"
          />
        </div>
      </div>
    </div>
  );
}
